export interface IRecvAudioStats {
  timestamp?: number
  packetsLost?: number
  packetsReceived?: number
  bytesReceived?: number
  jitterBufferDelay?: number
  jitterBufferEmittedCount?: number
  totalSamplesReceived?: number
  concealedSamples?: number
  silentConcealedSamples?: number
  concealmentEvents?: number
  fecPacketsReceived?: number
  estimatedPlayoutTimestamp?: number
  totalSamplesDuration?: number
  recordSampleRate?: number
  numChannels?: number
  codecType?: string
  rtt?: number
  total_rtt?: number
}

export interface IRecvVideoStats {
  timestamp?: number
  packetsLost?: number
  packetsReceived?: number
  bytesReceived?: number
  framesDecoded?: number
  totalDecodeTime?: number
  framesReceived?: number
  keyFramesDecoded?: number
  framesDropped?: number
  firCount?: number
  nackCount?: number
  pliCount?: number
  jitterBufferDelay?: number
  jitterBufferEmittedCount?: number
  width?: number
  height?: number
  estimatedPlayoutTimestamp?: number
  codecType?: string
  rtt?: number
  totalRtt?: number
}

export interface IAudioStats {
  rtt: number
  audioLossRate: number
  //  stallDuration: number,
  e2eDelay: number
  statsInterval: number
  receivedKBitrate: number
  jitterBufferDelay: number
  packetsReceived: number
  numChannels: number
  estimatedPlayoutTimestamp?: number
  samplesDuration: number
  receivedSampleRate: number
  concealedSamples: number
  concealmentEvent: number
}

export interface IVideoStats {
  rtt: number
  videoLossRate: number
  e2eDelay: number
  statsInterval: number
  receivedKBitrate: number
  retransmittedRate: number
  jitterBufferDelay: number
  width: number
  height: number
  estimatedPlayoutTimestamp?: number
  decoderOutputFrameRate: number
  receivedFrameRate: number
  codecType?: string
  packetsReceived: number
  avgDecodeDelay: number
  keyFramesDecoded: number
  framesDropped: number
  firCount: number
  nackCount: number
  pliCount: number
  totalRtt?: number
  // 升档、降档、或者为空
  abrState: 'upgrade' | 'degrade' | 'none'
}

export async function getRecvAudioStats (getStats: Promise<RTCStatsReport>) {
  const currentStats: IRecvAudioStats = {}
  const result = await getStats
  currentStats.timestamp = Date.now()
  result?.forEach(stat => {
    if (stat.type === 'inbound-rtp') {
      currentStats.packetsLost = stat.packetsLost
      currentStats.packetsReceived = stat.packetsReceived
      currentStats.bytesReceived = stat.bytesReceived
      if (stat.jitterBufferDelay !== undefined) {
        currentStats.jitterBufferDelay = stat.jitterBufferDelay
        currentStats.jitterBufferEmittedCount = stat.jitterBufferEmittedCount
      }
      if (stat.totalSamplesReceived !== undefined) {
        currentStats.totalSamplesReceived = stat.totalSamplesReceived
        currentStats.concealedSamples = stat.concealedSamples
        currentStats.silentConcealedSamples = stat.silentConcealedSamples
        currentStats.concealmentEvents = stat.concealmentEvents
      }
      if (stat.fecPacketsReceived !== undefined) {
        currentStats.fecPacketsReceived = stat.fecPacketsReceived
      }
      if (stat.estimatedPlayoutTimestamp !== undefined) {
        currentStats.estimatedPlayoutTimestamp = stat.estimatedPlayoutTimestamp
      }
      if (stat.totalSamplesDuration !== undefined) {
        currentStats.totalSamplesDuration = stat.totalSamplesDuration
      }
    } else if (stat.type === 'codec') {
      currentStats.recordSampleRate = stat.clockRate
      if (stat.channels) {
        currentStats.numChannels = stat.channels
      }
      currentStats.codecType = stat.mimeType
    } else if (stat.type === 'candidate-pair') {
      currentStats.rtt = stat.currentRoundTripTime * 1000
      currentStats.total_rtt = stat.totalRoundTripTime * 1000
    } else if (stat.type === 'track' && stat.audioLevel !== undefined) {
      // ...
    } else if (stat.type === 'ssrc') {
      // ...
    }
  })
  return currentStats
}

export function diffRecvAudioStats (oldStats: IRecvAudioStats, newStats: IRecvAudioStats): IAudioStats {
  const statsInterval = diffNumber(oldStats, newStats, 'timestamp')
  const packetsLostDiff = diffNumber(oldStats, newStats, 'packetsLost')
  const jitterBufferDelay = Math.round(diffNumber(oldStats, newStats, 'jitterBufferDelay') / diffNumber(oldStats, newStats, 'jitterBufferEmittedCount') * 1000)
  const packetsReceived = diffNumber(oldStats, newStats, 'packetsReceived')
  return {
    audioLossRate: +(packetsLostDiff / (packetsReceived + packetsLostDiff)).toFixed(2),
    receivedKBitrate: +((diffNumber(oldStats, newStats, 'bytesReceived') * 8) / statsInterval).toFixed(2),
    statsInterval,
    rtt: newStats.rtt || 0,
    jitterBufferDelay,
    packetsReceived,
    numChannels: newStats.numChannels || 1,
    estimatedPlayoutTimestamp: newStats.estimatedPlayoutTimestamp,
    samplesDuration: Math.round(diffNumber(oldStats, newStats, 'totalSamplesDuration') * 1000),
    receivedSampleRate: diffNumber(oldStats, newStats, 'totalSamplesReceived') / statsInterval,
    concealedSamples: Math.round(diffNumber(oldStats, newStats, 'concealedSamples')),
    concealmentEvent: Math.round(diffNumber(oldStats, newStats, 'concealmentEvents')),
    e2eDelay: Math.round((newStats.rtt || 0) + jitterBufferDelay)
  }
}

export async function getRecvVideoStats (getStats: Promise<RTCStatsReport>) {
  const currentStats: IRecvVideoStats = {}
  const result = await getStats
  currentStats.timestamp = Date.now()
  result?.forEach(stat => {
    if (stat.type === 'inbound-rtp') {
      currentStats.packetsLost = stat.packetsLost
      currentStats.packetsReceived = stat.packetsReceived
      currentStats.bytesReceived = stat.bytesReceived
      currentStats.framesDecoded = stat.framesDecoded
      currentStats.totalDecodeTime = stat.totalDecodeTime
      currentStats.framesReceived = stat.framesReceived
      currentStats.keyFramesDecoded = stat.keyFramesDecoded
      currentStats.framesDropped = stat.framesDropped
      currentStats.firCount = stat.firCount
      currentStats.nackCount = stat.nackCount
      currentStats.pliCount = stat.pliCount
      if (stat.jitterBufferDelay !== undefined) {
        currentStats.jitterBufferEmittedCount = stat.jitterBufferEmittedCount
        currentStats.jitterBufferDelay = stat.jitterBufferDelay
      }
      if (stat.frameWidth !== undefined) {
        currentStats.width = stat.frameWidth
        currentStats.height = stat.frameHeight
      }
      if (stat.estimatedPlayoutTimestamp !== undefined) {
        currentStats.estimatedPlayoutTimestamp = stat.estimatedPlayoutTimestamp
      }
    } else if (stat.type === 'track' && stat.frameWidth !== undefined) {
      currentStats.width = stat.frameWidth
      currentStats.height = stat.frameHeight
      if (stat.framesReceived !== undefined) {
        currentStats.framesReceived = stat.framesReceived
      }
    } else if (stat.type === 'codec') {
      currentStats.codecType = stat.mimeType
    } else if (stat.type === 'candidate-pair') {
      currentStats.rtt = stat.currentRoundTripTime * 1000
      currentStats.totalRtt = stat.totalRoundTripTime * 1000
    }
  })
  return currentStats
}

export function diffRecvVideoStats (oldStats: IRecvVideoStats, newStats: IRecvVideoStats): IVideoStats {
  const statsInterval = diffNumber(oldStats, newStats, 'timestamp')
  const packetsLostDiff = diffNumber(oldStats, newStats, 'packetsLost')
  const nackCount = diffNumber(oldStats, newStats, 'nackCount')
  const packetsReceived = diffNumber(oldStats, newStats, 'packetsReceived')
  const jitterBufferDelay = Math.round(diffNumber(oldStats, newStats, 'jitterBufferDelay') / diffNumber(oldStats, newStats, 'jitterBufferEmittedCount') * 1000)
  const newResolution = (newStats.width || 0) * (newStats.height || 0)
  const oldResolution = (oldStats.width || 0) * (oldStats.height || 0)
  const abrState =
    newResolution === 0 || oldResolution === 0
      ? 'none'
      : newResolution > oldResolution
      ? 'upgrade'
      : newResolution < oldResolution
      ? 'degrade'
      : 'none'

  return {
    statsInterval,
    abrState,
    width: newStats.width || 0,
    height: newStats.height || 0,
    estimatedPlayoutTimestamp: newStats.estimatedPlayoutTimestamp,
    videoLossRate: +(packetsLostDiff / (diffNumber(oldStats, newStats, 'packetsReceived') + packetsLostDiff)),
    receivedKBitrate: +((diffNumber(oldStats, newStats, 'bytesReceived') * 8) / statsInterval).toFixed(2),
    decoderOutputFrameRate: Math.round((diffNumber(oldStats, newStats, 'framesDecoded') * 1000) / statsInterval),
    receivedFrameRate: Math.round((diffNumber(oldStats, newStats, 'framesReceived') * 1000) / statsInterval),
    rtt: newStats.rtt || 0,
    totalRtt: Math.round(newStats.totalRtt || 0),
    jitterBufferDelay,
    codecType: newStats.codecType,
    packetsReceived,
    avgDecodeDelay: Math.round((diffNumber(oldStats, newStats, 'totalDecodeTime') * 1000) / diffNumber(oldStats, newStats, 'framesDecoded')),
    keyFramesDecoded: diffNumber(oldStats, newStats, 'keyFramesDecoded'),
    framesDropped: diffNumber(oldStats, newStats, 'framesDropped'),
    firCount: diffNumber(oldStats, newStats, 'firCount'),
    nackCount,
    pliCount: diffNumber(oldStats, newStats, 'pliCount'),
    e2eDelay: Math.round((newStats.rtt || 0) + jitterBufferDelay),
    retransmittedRate: +(nackCount / (packetsReceived + packetsLostDiff)).toFixed(2),
  }
}

function diffNumber (oldValue: any, newValue: any, key: any) {
  const oldV = Number.isNaN(oldValue[key]) ? 0 : +(oldValue[key] as number)
  const newV = Number.isNaN(newValue[key]) ? 0 : +(newValue[key] as number)
  return Math.max(0, newV - oldV)
}